import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Alert from "@mui/material/Alert";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import DownloadIcon from "@mui/icons-material/Download";
import { useNavigate, useParams } from "react-router-dom";
import { useCompetitorLinkRemovalDetail } from "./useCompetitorLinkRemovalDetail";
import { useCompetitorLinkRemovalActions } from "./useCompetitorLinkRemovalActions";
import { RunProgressPanel } from "./RunProgressPanel";
import { RunResultsTable } from "./RunResultsTable";
import { RunLogPanel } from "./RunLogPanel";
import { RunLogExport } from "./RunLogExport";
import { useFileDownload } from "../../ui/useFileDownload";
import { IfPermission } from "../../auth/IfPermission";
import { isCancellable } from "./IsCancellable";
import type { JSX } from "react";

export function CompetitorLinkRemovalDetail() {
  let competitorLinkRemovalDetail: JSX.Element;
  const { id: removalId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { state } = useCompetitorLinkRemovalDetail(removalId);
  const { confirmState, cancelState, exportState, confirmRemoval, cancelRemoval, exportResults } =
    useCompetitorLinkRemovalActions(removalId);
  const downloadFile = useFileDownload();
  if (state.kind === "loading") {
    competitorLinkRemovalDetail = (
      <Box display="flex" justifyContent="center" alignItems="center" p={6}>
        <CircularProgress />
      </Box>
    );
  } else {
    if (state.kind === "error") {
      competitorLinkRemovalDetail = (
      <Box p={3}>
        <Box mb={2}>
          <Button startIcon={<ArrowBackIcon />} onClick={() => {
      void navigate("/competitor-link-removals");
    }}>
            К списку прогонов
          </Button>
        </Box>
        <Alert severity="error">Ошибка подключения. Переподключение…</Alert>
      </Box>
    );
    } else {
      const { snapshot, rows, logs } = state;
      const showConfirm = snapshot.status === "awaiting_confirmation";
      const showCancel = isCancellable(snapshot.status);
      competitorLinkRemovalDetail = (
    <Box p={3}>
      <Box mb={2} display="flex" gap={1} alignItems="center" flexWrap="wrap">
        <Button startIcon={<ArrowBackIcon />} onClick={() => {
      void navigate("/competitor-link-removals");
    }}>
          К списку прогонов
        </Button>

        <IfPermission permission="competitor-link-removals.manage">
          {showConfirm && (
            <Button
              variant="contained"
              color="primary"
              onClick={confirmRemoval}
              disabled={confirmState.kind === "loading"}
            >
              Подтвердить удаление
            </Button>
          )}
          {showCancel && (
            <Button
              variant="outlined"
              color="error"
              onClick={cancelRemoval}
              disabled={cancelState.kind === "loading"}
            >
              Отменить прогон
            </Button>
          )}
        </IfPermission>

        <IfPermission permission="competitor-link-removals.export">
          <Button
            variant="outlined"
            startIcon={<DownloadIcon />}
            onClick={exportResults}
            disabled={exportState.kind === "loading"}
          >
            Экспорт результатов (CSV)
          </Button>
        </IfPermission>
      </Box>

      {confirmState.kind === "error" && (
        <Alert severity="error" sx={{ mb: 2 }}>{confirmState.message}</Alert>
      )}
      {cancelState.kind === "error" && (
        <Alert severity="error" sx={{ mb: 2 }}>{cancelState.message}</Alert>
      )}
      {exportState.kind === "error" && (
        <Alert severity="error" sx={{ mb: 2 }}>{exportState.message}</Alert>
      )}

      <RunProgressPanel snapshot={snapshot} />
      <RunLogPanel
        logs={logs}
        onDownloadLog={() => {
          if (removalId !== undefined) {
            const content = RunLogExport.content(logs);
            const file = new Blob([content], { type: "text/plain;charset=utf-8" });
            downloadFile(file, RunLogExport.fileName(removalId));
          }
        }}
      />
      <RunResultsTable rows={rows} />
    </Box>
  );
    }
  }
  return competitorLinkRemovalDetail;
}
